// Shared UI primitives. One modal, one field, one person picker — used by every module.
import { useMemo, useRef, useState, useEffect, useLayoutEffect, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { initials, searchKey, type Person } from '@vero/domain';
import type { AttentionItem } from '@vero/intelligence';
import type { I18n } from '@vero/i18n';
import { useRuntime } from './runtime.tsx';
import { Icon } from './icons.tsx';
import { hueOf } from './viz.tsx';

export function Modal({ title, children, footer, wide, onClose }: { title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean; onClose?: () => void }) {
  const rt = useRuntime();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { const el = ref.current?.querySelector<HTMLElement>('input:not([type=hidden]),textarea,select,button.btn-primary'); el?.focus(); }, []);
  return (
    <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} ref={ref}>
      <div className="modal-head">
        <h2>{title}</h2>
        <button className="btn btn-icon btn-ghost" aria-label={rt.t('common.close')} onClick={() => { onClose?.(); rt.close(); }}><Icon name="close" /></button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-foot">{footer}</div>}
    </div>
  );
}

/** Labelled form field. `group` renders a fieldset-like div (for segmented buttons / multiple controls),
 *  because a <label> would hijack the accessible name of every nested button. */
export function Field({ label, children, span2, hint, group }: { label: ReactNode; children: ReactNode; span2?: boolean; hint?: ReactNode; group?: boolean }) {
  const cls = `field${span2 ? ' span2' : ''}`;
  if (group) return <div className={cls} role="group" aria-label={typeof label === 'string' ? label : undefined}><span>{label}</span>{children}{hint && <small className="muted small">{hint}</small>}</div>;
  return (
    <label className={cls}>
      <span>{label}</span>
      {children}
      {hint && <small className="muted small">{hint}</small>}
    </label>
  );
}

/** Person avatar: initials on a hue derived from the person (stable across sessions and devices). */
export function Avatar({ name, size, ring, seed }: { name: string; size?: 'xs' | 'sm' | 'lg' | 'xl'; ring?: string; seed?: string }) {
  return <div className={`avatar${size ? ' ' + size : ''}${ring ? ' ring-' + ring : ''}`} style={{ ['--h' as string]: hueOf(seed ?? name) }} aria-hidden="true">{initials(name)}</div>;
}

export function Empty({ icon = 'spark', children }: { icon?: string; children: ReactNode }) {
  return <div className="empty"><Icon name={icon} /><div>{children}</div></div>;
}

export function SeverityDot({ severity }: { severity?: string }) { return <span className={`dot ${severity ?? ''}`} aria-hidden="true" />; }

/** Translate an attention item (params may themselves be i18n keys, e.g. case stage). */
export function reasonText(i18n: I18n, it: AttentionItem): string {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(it.params)) {
    if (typeof v !== 'string') { params[k] = v; continue; }
    if (k === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(v)) params[k] = i18n.date(v, 'dayMonth');
    else if (k === 'time' && /^\d{2}:\d{2}$/.test(v)) params[k] = i18n.time(v);
    else params[k] = i18n.maybe(v);
  }
  return i18n.t(it.reasonKey, params);
}

/** Person search/select used in every form ("search everywhere", v602). */
export function PersonPicker({ value, onChange, includeDependents = false, autoFocus }: { value?: string | null; onChange: (id: string | null) => void; includeDependents?: boolean; autoFocus?: boolean }) {
  const rt = useRuntime();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = value ? rt.view.get('persons', value) : undefined;
  const results = useMemo(() => {
    const k = searchKey(q);
    return rt.view.rows('persons')
      .filter((p) => !p.archivedAt && (includeDependents || p.stage !== 'dependent'))
      .filter((p) => !k || searchKey(`${p.displayName} ${p.primaryPhone ?? ''} ${p.primaryEmail ?? ''}`).includes(k))
      .sort((a, b) => rt.i18n.compare(a.displayName, b.displayName))
      .slice(0, 8);
  }, [q, rt.view, includeDependents, rt.i18n]);
  if (selected && !open) {
    return (
      <div className="row input" style={{ cursor: 'pointer' }} onClick={() => setOpen(true)} role="button" tabIndex={0}>
        <Avatar name={selected.displayName} size="sm" />
        <span className="grow person-name one-line">{selected.displayName}</span>
        <button type="button" className="btn btn-sm btn-ghost" onClick={(e: MouseEvent) => { e.stopPropagation(); onChange(null); }}>{rt.t('common.edit')}</button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input className="input" autoFocus={autoFocus} placeholder={rt.t('common.select_person')} value={q} onChange={(e: { target: HTMLInputElement }) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 160)} aria-label={rt.t('common.person')} />
      {open && results.length > 0 && (
        <div className="card" style={{ position: 'absolute', insetInline: 0, zIndex: 5, marginTop: 4, padding: 4, maxHeight: 280, overflow: 'auto' }}>
          {results.map((p: Person) => (
            <div key={p.id} className="lens-item" onMouseDown={(e: MouseEvent) => { e.preventDefault(); onChange(p.id); setQ(''); setOpen(false); }}>
              <Avatar name={p.displayName} size="sm" />
              <div className="grow"><div className="person-name one-line">{p.displayName}</div><div className="tiny muted">{[rt.i18n.enumLabel('stage', p.stage), p.primaryPhone].filter(Boolean).join(' · ')}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Stat({ value, label, onClick }: { value: ReactNode; label: ReactNode; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'div';
  return <Tag className="stat" onClick={onClick}><b>{value}</b><span>{label}</span></Tag>;
}

export function useConfirm() {
  const rt = useRuntime();
  return (text: string, onYes: () => void, danger = false) => rt.open(
    <Modal title={rt.t('common.confirm')} footer={<><button className="btn" onClick={rt.close}>{rt.t('common.cancel')}</button><button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => { rt.close(); onYes(); }}>{rt.t('common.confirm')}</button></>}>
      <p style={{ margin: 0 }}>{text}</p>
    </Modal>,
  );
}

/** Time-of-day greeting part */
export function dayPart(d = new Date()): 'morning' | 'afternoon' | 'evening' { const h = d.getHours(); return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening'; }

/** Quick date shortcuts (spec §14): Today · Tomorrow · +3 days · +1 week. */
export function QuickDates({ value, onChange }: { value: string; onChange: (d: string) => void }) {
  const rt = useRuntime();
  const opts: [string, number][] = [['common.today', 0], ['common.tomorrow', 1], ['dates.plus3', 3], ['dates.plus7', 7]];
  const add = (n: number) => { const d = new Date(`${rt.today}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
  return (
    <div className="row wrap" style={{ gap: 4 }} role="group">
      {opts.map(([k, n]) => { const d = add(n); return <button key={k} type="button" className="chip" aria-pressed={value === d} onClick={() => onChange(d)}>{rt.t(k)}</button>; })}
    </div>
  );
}

/** Segmented control over a small set of values, labels from i18n. */
export function Seg<T extends string>({ values, value, onChange, label, testId }: { values: readonly T[]; value: T; onChange: (v: T) => void; label: (v: T) => string; testId?: string }) {
  return (
    <div className="segmented" role="group" data-testid={testId}>
      {values.map((v) => <button key={v} type="button" aria-pressed={v === value} onClick={() => onChange(v)}>{label(v)}</button>)}
    </div>
  );
}

/** Numeric input that keeps "" for empty (never silently 0). */
export function NumInput({ value, onChange, step = 1, testId, placeholder }: { value: number | null | undefined; onChange: (n: number | null) => void; step?: number; testId?: string; placeholder?: string }) {
  return <input className="input num" type="number" inputMode="decimal" step={step} value={value === null || value === undefined ? '' : String(value)} placeholder={placeholder} data-testid={testId}
    onChange={(e: { target: HTMLInputElement }) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
}

/** Page header used by every module — title, subtitle, actions (one layout authority). */
export function PageHead({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return <div className="page-head"><div style={{ minWidth: 0 }}><h1>{title}</h1>{subtitle && <p>{subtitle}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</div>;
}

/** A person cell used in every list: avatar + name, click opens Relationship Focus. */
export function PersonCell({ id, sub }: { id?: string | null; sub?: ReactNode }) {
  const rt = useRuntime();
  const p = id ? rt.view.get('persons', id) : undefined;
  if (!p) return <span className="muted">—</span>;
  return (
    <span className="row" style={{ gap: 8, minWidth: 0 }}>
      <Avatar name={p.displayName} size="xs" />
      <span style={{ minWidth: 0 }}><a className="person-name one-line" style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 600, display: 'block' }} onClick={(e: MouseEvent) => { e.stopPropagation(); location.hash = `#/people/${p.id}`; }}>{p.displayName}</a>{sub && <span className="tiny muted">{sub}</span>}</span>
    </span>
  );
}

/** one choice of an action menu: label key, icon, what it does, optional test id */
export type FanItem = [string, string, () => void, string?];
/** a titled group of choices (the title is a label key) */
export interface FanGroup { title: string; items: FanItem[] }

/**
 * Quick Action Fan (spec §40.7): secondary actions one click away, without a wall of buttons.
 * Drawn on top of the page (a portal with a fixed position), so no card with hidden overflow or its own layer can cut
 * it off or cover it. On a computer it opens under the button (above when there is more room there), in two columns
 * when the list is long, sized after measuring its real height. On a phone it is a sheet at the bottom of the screen
 * with a title and a close button, scrolling inside when it is taller than the screen. Keyboard: arrows, Home/End,
 * Escape (focus returns to the button). Choices can be given as one list (`items`) or in titled groups (`groups`).
 */
export function ActionFan({ items, groups, label, testId = 'action-fan' }: { items?: FanItem[]; groups?: FanGroup[]; label?: string; testId?: string }) {
  const rt = useRuntime();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const [sheet, setSheet] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const list: FanGroup[] = groups ?? [{ title: '', items: items ?? [] }];
  const count = list.reduce((n, g) => n + g.items.length, 0);
  const cols = (vw: number) => (count > 8 && vw >= 760 ? 2 : 1);
  const place = () => {
    const b = btn.current?.getBoundingClientRect();
    if (!b) return;
    const vw = window.innerWidth, vh = window.innerHeight, gap = 6, margin = 10;
    if (vw < 560) { setSheet(true); setPos({ position: 'fixed', left: 8, right: 8, bottom: 8, maxHeight: Math.round(vh * 0.86), overflowY: 'auto' }); return; }
    setSheet(false);
    const c = cols(vw);
    const width = Math.min(Math.max(c * 270, b.width), vw - 2 * margin);
    const rtl = document.documentElement.dir === 'rtl';
    let left = rtl ? b.left : b.right - width;
    left = Math.max(margin, Math.min(left, vw - width - margin));
    // the menu's real height (measured after the first draw), else an estimate
    const measured = menu.current ? menu.current.scrollHeight : 0;
    const est = (Math.ceil(count / c) + 1) * 42 + (groups ? Math.ceil(list.length / c) * 30 : 0) + 16;
    const want = Math.min(measured || est, vh - 2 * margin);
    const below = vh - b.bottom - gap - margin, above = b.top - gap - margin;
    const base: CSSProperties = { position: 'fixed', left, width, overflowY: 'auto' };
    if (below >= want) setPos({ ...base, top: b.bottom + gap, maxHeight: want });
    else if (above >= want) setPos({ ...base, bottom: vh - b.top + gap, maxHeight: want });
    // neither side holds it: the list covers the button rather than hiding choices behind a scroll
    else setPos({ ...base, top: Math.max(margin, Math.min(b.top + b.height / 2 - want / 2, vh - margin - want)), maxHeight: want });
  };
  useLayoutEffect(() => { if (open) place(); else setPos(null); }, [open]);
  // second pass, once the menu exists: place it again with its measured height
  useLayoutEffect(() => { if (open && pos && !sheet && menu.current && !menu.current.dataset.measured) { menu.current.dataset.measured = '1'; place(); } }, [open, !!pos]);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => { const t = e.target as Node; if (!ref.current?.contains(t) && !menu.current?.contains(t)) setOpen(false); };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); btn.current?.focus(); return; }
      const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]') ?? [])];
      if (!all.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const i = all.indexOf(document.activeElement as HTMLButtonElement);
      const n = e.key === 'Home' ? 0 : e.key === 'End' ? all.length - 1 : e.key === 'ArrowDown' ? (i + 1) % all.length : (i - 1 + all.length) % all.length;
      all[n]!.focus();
    };
    // the page scrolling (not the menu itself) moves the button: follow it
    const follow = (e: Event) => { if (e.type === 'scroll' && menu.current?.contains(e.target as Node)) return; place(); };
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', key);
    window.addEventListener('resize', follow); window.addEventListener('scroll', follow, true);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', key); window.removeEventListener('resize', follow); window.removeEventListener('scroll', follow, true); };
  }, [open]);
  useEffect(() => { if (open && pos) menu.current?.querySelector<HTMLButtonElement>('[role=menuitem]')?.focus({ preventScroll: true }); }, [open, !!pos]);
  const title = label ?? rt.t('common.more');
  // groups go to the shorter column, in order (a plain list is split in half); one column on a phone
  const columns: FanGroup[][] = (() => {
    const c = sheet || typeof window === 'undefined' ? 1 : cols(window.innerWidth);
    if (c === 1) return [list];
    if (!groups) { const half = Math.ceil(count / 2); return [[{ title: '', items: list[0]!.items.slice(0, half) }], [{ title: '', items: list[0]!.items.slice(half) }]]; }
    const out: FanGroup[][] = [[], []]; const h = [0, 0];
    for (const g of list) { const i = h[0]! <= h[1]! ? 0 : 1; out[i]!.push(g); h[i]! += g.items.length + 1; }
    return out;
  })();
  return (
    <div className="fan" ref={ref}>
      <button ref={btn} className="btn btn-sm" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)} data-testid={testId}><Icon name="more" />{title}</button>
      {open && pos && createPortal(
        <>
          {sheet && <div className="fan-scrim" aria-hidden="true" onPointerDown={() => setOpen(false)} />}
          <div className={`fan-menu is-floating${sheet ? ' is-sheet' : ''}${groups ? ' has-groups' : ''}`} role="menu" aria-label={title} ref={menu} style={pos} data-testid={`${testId}-menu`}>
            {sheet && <div className="fan-sheet-head"><b>{title}</b><button className="btn btn-icon btn-sm" aria-label={rt.t('common.close')} onClick={() => { setOpen(false); btn.current?.focus(); }}><Icon name="close" /></button></div>}
            <div className="fan-cols" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
              {columns.map((col, ci) => (
                <div key={ci} className="fan-col">
                  {col.map((g, gi) => (
                    <div key={g.title || gi} className="fan-group" role={groups ? 'group' : undefined} aria-label={g.title ? rt.t(g.title) : undefined}>
                      {g.title && <div className="fan-group-title">{rt.t(g.title)}</div>}
                      {g.items.map(([k, icon, run, tid]) => <button key={k} role="menuitem" data-testid={tid} onClick={() => { setOpen(false); run(); }}><Icon name={icon} size={15} /><span>{rt.t(k)}</span></button>)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body)}
    </div>
  );
}

/** Totals per currency, formatted ("€1,200.00 · £300.00"). Different currencies are never added together. */
export function fmtTotals(i18n: I18n, totals: Record<string, number>, opts?: { compact?: boolean }): string {
  const keys = Object.keys(totals);
  if (!keys.length) return i18n.money(0, undefined, opts);
  return keys.map((c) => i18n.money(totals[c]!, c, opts)).join(' · ');
}
